import { Spacing } from '@persona-id/design-tokens';
import { useMemo, useState } from 'react';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { AccountType } from 'dashboard/models/account-type';
import { DatumValueType } from 'dashboard/models/graph';
import { PresetType, ServerPresetSummary, SourceObjectType } from 'dashboard/models/graph-backend';
import { DatumTypeSource } from 'dashboard/models/graph-datum-type';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { FieldSchemaType } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

import { NewGraphNodeFormData } from './FormCreateGraphNode';

export interface BasicNodeInfoProps {
  control: Control<NewGraphNodeFormData, object>;
  watch: UseFormWatch<NewGraphNodeFormData>;
  register: UseFormRegister<NewGraphNodeFormData>;
  setValue: UseFormSetValue<NewGraphNodeFormData>;
  accountTypes: AccountType[];
  presetSummaries: ServerPresetSummary[];
}

const Container = styled.div`
  padding: ${Spacing[5]};
`;

const getSourceOptions = (accountTypes: AccountType[]): OptionType[] => {
  const options: OptionType[] = [];
  accountTypes.forEach((accountType) => {
    options.push({ label: accountType.attributes.name, value: accountType.id });

    const arrayTypes =
      accountType.attributes['field-schemas'].filter((fs) => fs.type === FieldSchemaType.Array) ||
      [];
    arrayTypes.forEach((arrayType) => {
      options.push({
        label: `${accountType.attributes.name}.${arrayType.key}`,
        value: `${accountType.id}.${arrayType.key}`,
      });
    });
  });
  return options;
};

const sourceToOptionString = (source: DatumTypeSource): string => {
  return source.path
    ? `${source['object-type-token']}.${source['path']}`
    : source['object-type-token'];
};

const updateSources = (
  existingSources: DatumTypeSource[],
  optionValues: string[],
): DatumTypeSource[] => {
  const optionStringToSource: Record<string, DatumTypeSource> = {};
  existingSources.forEach((source) => {
    optionStringToSource[sourceToOptionString(source as DatumTypeSource)] = source;
  });

  const updatedSources: DatumTypeSource[] = [];
  optionValues.forEach((value) => {
    if (value in optionStringToSource) {
      updatedSources.push(optionStringToSource[value]);
    } else {
      const parts = value.split('.');
      updatedSources.push({
        'object-type-token': parts[0],
        'object-type': SourceObjectType.Account,
        path: parts[1] || '',
      });
    }
  });

  return updatedSources;
};

const filterAccountTypeByFieldSchemaKey = (
  accountTypes: AccountType[],
  fsKey: string,
): AccountType[] => {
  return accountTypes.filter((accountType) => {
    const filtered = accountType.attributes['field-schemas'].find(
      (fieldSchema) => fieldSchema.key === fsKey,
    );
    return !!filtered;
  });
};

const BasicNodeInfo = ({
  control,
  watch,
  register,
  setValue,
  accountTypes,
  presetSummaries,
}: BasicNodeInfoProps) => {
  const [nodeType, setNodeType] = useState<string>('');
  const { fields: sources, replace: replaceSources } = useFieldArray<
    NewGraphNodeFormData,
    'sources',
    'id'
  >({
    control,
    name: 'sources',
  });
  const datumType = watch('datumType');
  const allSourceOptions = useMemo(() => getSourceOptions(accountTypes), [accountTypes]);
  const selectedOptions = useMemo(() => sources.map((s) => sourceToOptionString(s)), [sources]);
  const handleUpdateSources = (values: string[]) => {
    const newSources = updateSources(sources, values);
    replaceSources(newSources);
  };

  const emailSimilarityEnabled = useFeatureFlag('dashboard:graph-email-similarity');
  const imgBackgroundSimilarityEnabled = useFeatureFlag(
    'dashboard:graph-image-background-similarity',
  );
  const nodeFunctionsWriteEnabled = useFeatureFlag(
    'dashboard:graph-config-node-functions-tab-enable-write',
  );

  const DATUM_TYPE_OPTIONS = [
    {
      value: DatumValueType.EmailSimilarity,
      label: 'Email Similarity',
      subtitle:
        'Apply standard email similarity match configs of Levenshtein Edit Distance 1 or 2, Alphabetic Character Equivalence, or Email Domain',
      disabled: !emailSimilarityEnabled,
    },
    {
      value: DatumValueType.ImageBackgroundSimilarity,
      label: 'Selfie Similarity (Image)',
      subtitle: 'Apply standard similarity match for images',
      disabled: !imgBackgroundSimilarityEnabled,
    },
    {
      value: 'node-functions-type',
      label: 'Node Functions',
      subtitle: 'Apply data transformations via functions and configure field level match criteria',
      disabled: !nodeFunctionsWriteEnabled,
    },
  ];

  return (
    <Container>
      <Label weight='bold'>Select node type</Label>
      <Spacer height={1} />
      <Controller
        control={control}
        name='datumType'
        defaultValue={datumType}
        rules={{ required: true }}
        render={({ field: { onChange } }) => {
          const handleChange = (value: string) => {
            if (value === 'node-functions-type') {
              onChange(null);
              setValue('presetType', presetSummaries?.[0]?.preset);
              setValue('sources', []);
            } else if (value === DatumValueType.EmailSimilarity) {
              onChange(value);
              setValue('presetType', PresetType.NormalizeSimilarityTargets);
              setValue(
                'sources',
                // NOTE: Hard coding for now until we support custom paths for email similarity
                filterAccountTypeByFieldSchemaKey(accountTypes, 'email_address').map(
                  (accountType) => ({
                    'object-type': 'Account',
                    'object-type-token': accountType.id,
                  }),
                ),
              );
            } else {
              onChange(value);
              setValue('presetType', PresetType.NormalizeSimilarityTargets);
              setValue(
                'sources',
                // NOTE: Hard coding for now until we support custom paths for image similarity
                filterAccountTypeByFieldSchemaKey(accountTypes, 'selfie_photo').map(
                  (accountType) => ({
                    'object-type': 'Account',
                    'object-type-token': accountType.id,
                  }),
                ),
              );
            }
            setNodeType(value);
          };
          return (
            <Select
              placeholder='Select node'
              value={nodeType}
              onValueChange={handleChange}
              aria-labelledby='datum-type-selector-label'
            >
              {DATUM_TYPE_OPTIONS.map((opt) => (
                <Option
                  key={opt.value}
                  value={opt.value}
                  subtitle={opt.subtitle}
                  disabled={opt.disabled}
                >
                  {opt.label}
                </Option>
              ))}
            </Select>
          );
        }}
      />
      <Spacer height={4} />
      <Label weight='bold'>Node Name</Label>
      <Spacer height={1} />
      <Paragraph>
        Enter a name for your node, you will be able to change the name in future steps.
      </Paragraph>
      <Spacer height={1} />
      <Input placeholder='Enter name' {...register('name')} />

      {nodeType === 'node-functions-type' && (
        <div>
          <Spacer height={4} />
          <Label weight='bold'>Select Account Source(s)</Label>
          <ReactSelect
            isMulti
            value={selectedOptions}
            options={allSourceOptions}
            onChange={handleUpdateSources}
            menuPlacement='bottom'
            preserveOrder
            portal
            styles={{
              menuPortal: (base) => ({
                ...base,
                pointerEvents: 'auto',
              }),
            }}
          />
        </div>
      )}
    </Container>
  );
};

export default BasicNodeInfo;
